<template>
  <div>
    <!-- Logs Modal -->
    <LogsModal :id="modalKeys.LOGS" :selected-customer="clickedCustomer" />
    <!-- Removal Modal -->
    <ActionPromptModal :id="modalKeys.REMOVE" :handle-submit="handleRemove">
      <template #modal-title>
        <div class="d-flex">
          <TrashSolidIcon
            style="width: fit-content; height: fit-content; align-self: center"
          />
          <h4 class="py-3 px-1 mb-0 font-weight-boldy">Remove Customers</h4>
        </div>
      </template>
      <template #modal-message>
        <p>
          This action will remove the selected customers from the whitelist.
        </p>
      </template>
    </ActionPromptModal>
    <!-- Re-addition Modal -->
    <ActionPromptModal :id="modalKeys.RE_ADD" :handle-submit="handleReAdd">
      <template #modal-title>
        <div class="d-flex">
          <RefreshIcon
            style="width: fit-content; height: fit-content; align-self: center"
          />
          <h4 class="py-3 px-1 mb-0 font-weight-boldy">Re-add Customers</h4>
        </div>
      </template>
      <template #modal-message>
        <p>
          This action will add customers which were previously removed to the
          whitelist.
        </p>
      </template>
    </ActionPromptModal>
    <!-- Header -->
    <b-row class="mt-4 mb-1">
      <b-col>
        <p style="max-width: 690px">
          Manage the list of whitelisted users. Whitelisted users will not
          appear in the spam SMS reports.
        </p>
      </b-col>
    </b-row>
    <!-- Add Form -->
    <b-row class="mb-3">
      <b-col cols="12" md="6" lg="5" xl="4" class="mb-sm-2">
        <R-Label :theme="labelTheme" value="Customer ID:" />
        <div class="py-1 d-flex flex-row align-items-center">
          <b-form-input
            v-model.trim="customerId"
            name="customerId"
            trim
            size="mr-8 md"
            class="w-100 mr-2"
          />
          <R-Button
            @onClick="addCustomer"
            class="fill-light"
            :isDisabled="showLoader"
          >
            <PlusCircleIcon style="transform: translateY(-2px)" />
          </R-Button>
        </div>
        <span
          v-if="customerIdError"
          class="small"
          style="color: rgb(225, 45, 45)"
          >{{ customerIdError }}</span
        >
      </b-col>
    </b-row>
    <!-- Table -->
    <b-row>
      <b-col cols="12" class="mt-3">
        <table-view
          ref="table"
          v-model="getWhitelist"
          :visible-columns="visibleColumns"
          :column-titles="columnTitles"
          :show-loader="showLoader"
          :formatter-cell="formatterCell"
          :is-editable="false"
          :has-filter-action-buttons="false"
          :has-set-action-buttons="false"
          :has-edit-action-buttons="false"
          :has-download="true"
          :has-submit="false"
          :hide-active-row-count="true"
          :_row-formatter="rowFormatter"
          @rowSelectionChanged="rowSelectionChanged"
          @cellClick="cellClicked"
        >
          <template #actionBar="{ hasSelection, getSelectedRows }">
            <b-row class="py-1 m-0 w-100">
              <b-col class="d-flex col-auto">
                <b-row align-v="center">
                  <R-Button
                    :variant="'destructive'"
                    :isDisabled="!canRemoveRows"
                    class="mr-2 fill-light bg-danger"
                    @onClick="openRemoveDialog(getSelectedRows)"
                    :hasShadow="true"
                    :size="'small'"
                  >
                    <div class="d-flex">
                      <span class="mr-2">Remove Selected</span>
                      <TrashSolidIcon class="d-flex svg-small-size" />
                    </div>
                  </R-Button>
                  <R-Button
                    :isDisabled="!canReAddRows"
                    class="fill-light"
                    @onClick="openReAddDialog(getSelectedRows)"
                    :hasShadow="true"
                    :size="'small'"
                  >
                    <div class="d-flex">
                      <span class="mr-2">Re-add Selected</span>
                      <RefreshIcon class="d-flex svg-small-size" />
                    </div>
                  </R-Button>
                </b-row>
              </b-col>
            </b-row>
          </template>
        </table-view>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import debounce from "lodash/debounce";
import { DURATION, notify, TYPE } from "@8x8/oxygen-toaster";
import Button from "@8x8/oxygen-button";
import Tooltip from "@8x8/oxygen-tooltip";

import { humanizeFromMilliseconds } from "@/vue/utils/time";
import TableView from "@/vue/components/tabulator/Index.vue";

import ActionPromptModal from "./ActionPromptModal";
import LogsModal from "./LogsModal";
import { MODAL_KEYS, ACTIONS } from "./constants";
import {
  processCustomer,
  wasCustomerRemoved,
  canPerformAction,
} from "./customer";

const COLUMNS = Object.freeze([
  { name: "customerId", label: "Customer ID" },
  { name: "customerName", label: "Customer Name" },
  { name: "addedDateTime", label: "Whitelisted at" },
  { name: "addedBy", label: "Whitelisted by" },
  { name: "removedDateTime", label: "Removed at" },
  { name: "removedBy", label: "Removed by" },
  // Action slots are mapped to actions
  { name: "showLogs", label: "Logs" },
]);
const DELETED_ROW_BACKGROUND_COLOR = "#f0dfdf";
const TIMESTAMP_FIELDS = Object.freeze(["addedDateTime", "removedDateTime"]);
const OPTIONAL_FIELDS = Object.freeze(["removedDateTime", "removedBy"]);

export default {
  name: "SmsWhitelist",
  components: {
    TableView,
    ActionPromptModal,
    LogsModal,
    RButton: Button,
    RTooltip: Tooltip,
  },
  data() {
    return {
      customerId: null,
      customerIdError: null,
      showLoader: false,
      modalKeys: MODAL_KEYS,
      labelTheme: { fontWeight: 600 },
      selectedCustomers: null,
      clickedCustomer: null,
    };
  },
  computed: {
    getWhitelist() {
      const customers = this.$store.state.smsaudit?.whitelistedCustomers ?? [];
      return customers.map((customer) => processCustomer(customer));
    },
    columnTitles() {
      return Object.fromEntries(COLUMNS.map((item) => [item.name, item.label]));
    },
    visibleColumns() {
      return COLUMNS.map((item) => item.name);
    },
    canRemoveRows() {
      return canPerformAction(this.selectedCustomers, ACTIONS.REMOVE);
    },
    canReAddRows() {
      return canPerformAction(this.selectedCustomers, ACTIONS.RE_ADD);
    },
  },
  mounted() {
    if (!this.getWhitelist?.length) {
      this.retrieveWhitelistedCustomers();
    }
  },
  methods: {
    ...mapActions([
      "fetchWhitelistedCustomers",
      "whitelistCustomerById",
      "removeFromWhitelist",
      "whitelistMultipleCustomers",
      "setShouldRefetchDiagnostics",
    ]),
    /**
     * Event listener for the rowSelectionChanged event in Tabulator.
     */
    rowSelectionChanged({ data }) {
      this.selectedCustomers = data;
    },
    openRemoveDialog() {
      this.$bvModal.show(MODAL_KEYS.REMOVE);
    },
    handleRemove: async function (reason) {
      this.showLoader = true;
      const customers = [...this.selectedCustomers];

      try {
        const { failedRequests } = await this.removeFromWhitelist({
          customers,
          reason,
        });
        if (failedRequests?.length) {
          failedRequests.forEach(console.error);

          notify({
            content: `Failed to remove ${failedRequests.length} / ${customers.length} customers.`,
            type: "error",
            duration: DURATION.INFINITE,
          });
        } else {
          notify({
            content: `Successfully removed ${customers.length} customers.`,
            type: "success",
            duration: 5000,
          });

          this.setShouldRefetchDiagnostics(true);
        }
      } catch (error) {
        console.error("Caught error during remove operation.", error);
      } finally {
        this.showLoader = false;
      }
    },
    openReAddDialog() {
      this.$bvModal.show(MODAL_KEYS.RE_ADD);
    },
    handleReAdd: async function (reason) {
      const customerIds = this.selectedCustomers.map((data) => data.customerId);

      try {
        this.showLoader = true;
        // We need to set ?force, otherwise the add request will be rejected
        const { failedRequests } = await this.whitelistMultipleCustomers({
          reason,
          customerIds,
          force: true,
        });

        if (failedRequests?.length) {
          failedRequests.forEach(console.error);

          notify({
            content: `Failed to re-add ${failedRequests.length} / ${customerIds.length} customers.`,
            type: "error",
            duration: DURATION.INFINITE,
          });
        } else {
          notify({
            content: `Successfully re-added ${customerIds.length} customers.`,
            type: "success",
            duration: 5000,
          });

          this.setShouldRefetchDiagnostics(true);
        }
      } catch (error) {
        console.log("Re-addition failed.", error);
      } finally {
        this.showLoader = false;
      }
    },
    formatterCell(cell) {
      const key = cell.getField();
      const value = cell.getValue();

      if (TIMESTAMP_FIELDS.includes(key) && value) {
        return humanizeFromMilliseconds(value);
      }

      if (["customerId"].includes(key)) {
        return `<small><pre class="m-0">${value}</pre></small>`;
      }

      if (OPTIONAL_FIELDS.includes(key) && !value?.length) {
        return `<small>n/a</small>`;
      }

      if ("showLogs" === key) {
        return `<span class="btn-link">View Logs</span>`;
      }

      return value;
    },
    async cellClicked(event, cell) {
      if (cell.getField() === "showLogs") {
        this.clickedCustomer = cell.getData();
        this.$bvModal.show(MODAL_KEYS.LOGS);
      }
    },
    rowFormatter(row) {
      const customerData = row.getData();
      if (wasCustomerRemoved(customerData)) {
        row.getElement().style.backgroundColor = DELETED_ROW_BACKGROUND_COLOR;
      }
    },
    retrieveWhitelistedCustomers: debounce(async function () {
      this.showLoader = true;

      try {
        await this.fetchWhitelistedCustomers();
      } catch (errorMessage) {
        notify({
          content: errorMessage,
          type: TYPE.error,
          duration: DURATION.INFINITE,
        });
      } finally {
        this.showLoader = false;
      }
    }, 1000),
    addCustomer: debounce(async function () {
      this.customerIdError = null;

      try {
        this.showLoader = true;
        await this.whitelistCustomerById(this.customerId);
        this.setShouldRefetchDiagnostics(true);
      } catch (errorMessage) {
        notify({
          content: errorMessage,
          type: TYPE.error,
          duration: DURATION.INFINITE,
        });

        this.customerIdError = errorMessage;
      } finally {
        this.showLoader = false;
      }
    }, 1000),
  },
};
</script>
